/**
 * treeIndex — 강의의 줄 나무 계산(순수 함수).
 *
 * 서버(P3)는 줄을 **평면 목록 · 전위 순회**(부모 다음 그 자식들, 형제는 order_no·id 순)로 준다(회신 §4-1).
 * 나무 조립은 화면 몫이라 여기서 부모별로 묶는다. 서버의 트리 서비스(services/syllabus.py)와 같은 규칙을 쓴다:
 *   - 부모가 목록에 없는 줄은 최상위로 본다(children).
 *   - 깊이는 최상위 = 1, 최대 5(MAX_DEPTH). 옮긴 뒤 서브트리의 깊이도 5를 넘으면 안 된다(check_move).
 *   - 자기 자신·자기 하위 줄 밑으로는 옮길 수 없다(cycle).
 */

export const MAX_DEPTH = 5;
export const MAX_NODES = 500;

const ROOT = 'root';
const keyOf = (id) => (id == null ? ROOT : String(id));

/**
 * 평면 목록 → 색인 { byId, children(부모 키 → 자식 id 배열), roots }.
 * 형제 순서는 order_no(서버 순번) 순 — 같거나 없으면 받은 순서 그대로(안정 정렬).
 * 쓰기 응답을 목록 끝에 덧붙여 반영해도(workspaceModel.apply*) 서버와 같은 순서로 선다.
 */
export function buildIndex(items) {
  const list = Array.isArray(items) ? items : [];
  const byId = new Map(list.map((it) => [String(it.node_id), it]));
  const children = new Map();
  list.forEach((it, pos) => {
    const parentKey = it.parent_id != null && byId.has(String(it.parent_id)) ? String(it.parent_id) : ROOT;
    if (!children.has(parentKey)) children.set(parentKey, []);
    children.get(parentKey).push({ id: String(it.node_id), o: Number.isFinite(it.order_no) ? it.order_no : null, pos });
  });
  const sorted = new Map();
  children.forEach((arr, key) => {
    const anyOrder = arr.some((e) => e.o != null);
    const out = anyOrder
      ? [...arr].sort((a, b) => ((a.o ?? Infinity) - (b.o ?? Infinity)) || (a.pos - b.pos))
      : arr;
    sorted.set(key, out.map((e) => e.id));
  });
  return { byId, children: sorted, roots: sorted.get(ROOT) || [] };
}

export const childrenOf = (index, id) => index.children.get(keyOf(id)) || [];

/** 부모 줄 id(최상위면 null) — 부모가 목록에 없으면 최상위로 본다 */
export function parentOf(index, id) {
  const it = index.byId.get(String(id));
  if (!it || it.parent_id == null || !index.byId.has(String(it.parent_id))) return null;
  return String(it.parent_id);
}

/** 조상 id — 최상위부터 부모까지(순환 방어) */
export function ancestorsOf(index, id) {
  const out = [];
  const seen = new Set([String(id)]);
  let cur = parentOf(index, id);
  while (cur != null && !seen.has(cur)) {
    seen.add(cur);
    out.unshift(cur);
    cur = parentOf(index, cur);
  }
  return out;
}

/** 깊이 — 최상위 = 1 */
export const depthOf = (index, id) => ancestorsOf(index, id).length + 1;

/** 자손 id — 자기 자신 제외, 부모 먼저(순환 방어) */
export function descendantsOf(index, id) {
  const out = [];
  const seen = new Set([String(id)]);
  const stack = [String(id)];
  while (stack.length) {
    childrenOf(index, stack.pop()).forEach((k) => {
      if (!seen.has(k)) {
        seen.add(k);
        out.push(k);
        stack.push(k);
      }
    });
  }
  return out;
}

/** 서브트리 높이 — 자기 자신 = 1 */
export function heightOf(index, id) {
  let h = 0;
  const seen = new Set([String(id)]);
  let frontier = [String(id)];
  while (frontier.length) {
    h += 1;
    const next = [];
    frontier.forEach((u) => childrenOf(index, u).forEach((k) => {
      if (!seen.has(k)) { seen.add(k); next.push(k); }
    }));
    frontier = next;
  }
  return h;
}

/**
 * 화면에 설 줄 — 전위 순회, 펼친 줄의 자식만. expanded 는 줄 id(문자열) 집합.
 * 반환 [{ item, id, depth, hasChildren, open }]
 */
export function visibleRows(index, expanded) {
  const out = [];
  const open = expanded instanceof Set ? expanded : new Set();
  const walk = (ids, depth) => {
    ids.forEach((id) => {
      const kids = childrenOf(index, id);
      const isOpen = kids.length > 0 && open.has(id);
      out.push({ item: index.byId.get(id), id, depth, hasChildren: kids.length > 0, open: isOpen });
      if (isOpen) walk(kids, depth + 1);
    });
  };
  walk(index.roots, 1);
  return out;
}

/**
 * 이 줄을 parentId 밑으로 옮길 수 있는가 — 서버 check_move 와 같은 자: 자기·자기 하위 밑은 안 되고(cycle),
 * 옮긴 뒤 서브트리 깊이가 5 를 넘으면 안 된다(max_depth). parentId 가 null 이면 최상위 — 이미 나무 안에 있던 가지라 늘 된다.
 * [옮기기…] 목록(moveTargets)과 끌어 놓기(dropDestination)가 이 한 자를 쓴다 — 두 벌로 두면 한쪽만 막는 자리가 생긴다.
 */
export function canMoveTo(index, id, parentId, maxDepth = MAX_DEPTH) {
  const self = String(id);
  if (!index.byId.has(self)) return false;
  if (parentId == null) return true;
  const p = String(parentId);
  if (!index.byId.has(p) || p === self || ancestorsOf(index, p).includes(self)) return false;
  return depthOf(index, p) + heightOf(index, self) <= maxDepth;
}

/**
 * 이 줄을 옮길 수 있는 상위 자리 — 최상위(null) + 자기·자기 하위가 아니고 옮긴 뒤 깊이가 5 이하인 줄.
 * 반환 [{ id: string|null, name, depth }] (최상위가 맨 앞, 나머지는 전위 순회 순서)
 */
export function moveTargets(index, id, maxDepth = MAX_DEPTH) {
  const out = [{ id: null, name: '최상위', depth: 0 }];
  const walk = (ids) => ids.forEach((k) => {
    if (canMoveTo(index, id, k, maxDepth)) out.push({ id: k, name: index.byId.get(k)?.name ?? '', depth: depthOf(index, k) });
    walk(childrenOf(index, k));
  });
  walk(index.roots);
  return out;
}

/**
 * 끌어 놓은 줄이 설 새 형제 순서 — beforeId 앞에(null 이거나 형제가 아니면 끝에) 끼운다. 자기는 원래 자리에서 빼고 센다.
 */
export function orderWithInsertBefore(siblings, id, beforeId) {
  const self = String(id);
  const list = (siblings || []).map(String).filter((k) => k !== self);
  const k = beforeId == null ? -1 : list.indexOf(String(beforeId));
  return k < 0 ? [...list, self] : [...list.slice(0, k), self, ...list.slice(k)];
}

/**
 * 끌어 놓기의 도착지 — { parentId, beforeId }(beforeId 가 null 이면 그 부모의 끝). 놓을 수 없거나 놓아도 그대로면 null.
 * targetId 가 null 이면 최상위의 끝(「최상위로」 띠). zone 은 줄 위 어느 자리에 놓았는가:
 *   'inside' 그 줄의 끝 하위로 · 'before' 그 줄 바로 위 형제로 · 'after' 바로 아래 형제로.
 * firstKid 는 그 줄이 펼쳐져 자식이 보일 때의 첫 자식이다 — 그때 줄의 아래 가장자리는 화면에서 **첫 자식 바로 위**라
 * 'after' 는 「첫 하위로」가 된다. 형제로 넣으면 선이 그어진 자리가 아니라 자식들을 전부 지나 그 뒤로 가 버린다.
 * 「놓아도 그대로」도 null 이다 — 받는 척(선·고리)을 하고 아무 일도 안 일어나면 화면이 거짓말을 한다.
 */
export function dropDestination(index, id, targetId, zone = 'inside', firstKid = null) {
  const self = String(id);
  if (!index.byId.has(self)) return null;
  const t = targetId == null ? null : String(targetId);
  let dest;
  if (t == null) dest = { parentId: null, beforeId: null };
  else if (!index.byId.has(t) || t === self) return null;
  else if (zone === 'inside') dest = { parentId: t, beforeId: null };
  else if (zone === 'after' && firstKid != null) dest = { parentId: t, beforeId: String(firstKid) };
  else {
    const p = parentOf(index, t);
    const sibs = childrenOf(index, p);
    dest = { parentId: p, beforeId: zone === 'before' ? t : (sibs[sibs.indexOf(t) + 1] ?? null) };
  }
  if (!canMoveTo(index, self, dest.parentId)) return null;
  // 「자기 앞」은 자기 바로 다음 형제 앞과 같다 — 그대로 두면 자기를 빼고 찾다가 끝으로 보내 버린다
  if (dest.beforeId === self) {
    const sibs = childrenOf(index, dest.parentId);
    dest = { ...dest, beforeId: sibs[sibs.indexOf(self) + 1] ?? null };
  }
  if (parentOf(index, self) === dest.parentId) {
    const cur = childrenOf(index, dest.parentId);
    const next = orderWithInsertBefore(cur, self, dest.beforeId);
    if (next.every((k, i) => k === cur[i])) return null;
  }
  return dest;
}

/** 형제 id(자기 포함, 서버 순서) */
export const siblingsOf = (index, id) => childrenOf(index, parentOf(index, id));

/** 형제 안에서 한 칸 위(-1)·아래(+1)로 — 끝이면 null */
export function reorderIds(siblings, id, dir) {
  const list = siblings.map(String);
  const k = list.indexOf(String(id));
  const j = k + dir;
  if (k < 0 || j < 0 || j >= list.length) return null;
  const next = [...list];
  [next[k], next[j]] = [next[j], next[k]];
  return next;
}

/** 이 줄 아래에 하위 줄을 더 둘 수 있는가(깊이 5 한도) */
export const canAddChild = (index, id) => depthOf(index, id) < MAX_DEPTH;

/**
 * 고른 줄이 보이도록 펼칠 집합 — 조상을 전부 연다(현재 가지만 펼침, 판 2 §4-2).
 * 이미 연 줄은 그대로 둔다(사용자가 연 다른 가지를 닫지 않는다).
 */
export function expandTo(index, id, expanded) {
  const next = new Set(expanded instanceof Set ? expanded : []);
  ancestorsOf(index, id).forEach((a) => next.add(a));
  return next;
}

export const childCount = (index, id) => childrenOf(index, id).length;

/** 줄의 자리 — 최상위 줄부터 그 줄까지의 이름(목록에 없는 줄이면 []) */
export function pathNames(index, id) {
  if (id == null || !index.byId.has(String(id))) return [];
  return [...ancestorsOf(index, id), String(id)].map((k) => index.byId.get(k)?.name ?? '');
}

/** 전위 순회로 처음 만나는 조건 줄(query 가 있는 줄) — 강의를 열 때 기본으로 고를 줄(설계 §W01). 없으면 null */
export function firstConditionId(index) {
  let found = null;
  const walk = (ids) => {
    ids.forEach((id) => {
      if (found != null) return;
      if (index.byId.get(id)?.query != null) { found = id; return; }
      walk(childrenOf(index, id));
    });
  };
  walk(index.roots);
  return found;
}

/**
 * 나무 안 찾기 — 이미 받은 줄의 이름과 extraText(id)(조건 요약 — 개념·라벨 이름)에서 글자를 찾는다.
 * 맞은 줄과 그 조상을 남긴다(어느 가지인지 보여야 한다). 빈 글자면 null(거르지 않음).
 * 서버 목록 검색 q 는 강의 이름만 찾으므로, 줄 내용 찾기는 여기 화면 몫이다(설계 §W01).
 */
export function filterIds(index, text, extraText = () => '') {
  const q = String(text ?? '').trim().toLowerCase();
  if (!q) return null;
  const matched = new Set();
  index.byId.forEach((it, id) => {
    const hay = `${it?.name ?? ''} ${extraText(id) ?? ''}`.toLowerCase();
    if (hay.includes(q)) matched.add(id);
  });
  const visible = new Set(matched);
  matched.forEach((id) => ancestorsOf(index, id).forEach((a) => visible.add(a)));
  return { matched, visible };
}

/**
 * 펼침 단계 — 진도 목록 머리의 [− N단 +]가 보는 값(오너 C안 2026-11-02).
 *   level   몇 단까지 **빠짐없이** 펼쳐졌나. 얕은 쪽부터 처음 접힌 줄의 깊이다(모두 펼쳤으면 maxDepth)
 *   visible 지금 화면에 선 가장 깊은 단 — 한 가지만 손으로 더 펼치면 level 보다 크다
 *   maxDepth 나무의 가장 깊은 단(줄이 없으면 0)
 * 고른 행의 조상이 저절로 열려 있어도 다른 가지가 접혀 있으면 level 은 오르지 않는다 — 「N단」은 전부 펼쳤다는 뜻이다.
 */
export function openLevelOf(index, expanded) {
  const open = expanded instanceof Set ? expanded : new Set();
  let maxDepth = 0;
  let firstClosed = Infinity;
  const walk = (ids, depth) => ids.forEach((id) => {
    if (depth > maxDepth) maxDepth = depth;
    const kids = childrenOf(index, id);
    if (!kids.length) return;
    if (!open.has(id) && depth < firstClosed) firstClosed = depth;
    walk(kids, depth + 1);
  });
  walk(index.roots, 1);
  const visible = visibleRows(index, open).reduce((m, r) => Math.max(m, r.depth), 0);
  return { level: Math.min(firstClosed, maxDepth), visible, maxDepth };
}

/** level 단까지 펼칠 집합 — 깊이가 level 보다 얕고 하위가 있는 줄. level 이 나무보다 깊으면 전체 펼침 */
export function idsToLevel(index, level) {
  const out = new Set();
  const walk = (ids, depth) => ids.forEach((id) => {
    const kids = childrenOf(index, id);
    if (!kids.length) return;
    if (depth < level) out.add(id);
    walk(kids, depth + 1);
  });
  walk(index.roots, 1);
  return out;
}

/** [+] 한 단 더 — 지금 level 단의 줄을 모두 연다. 손으로 더 펼쳐 둔 가지는 그대로 둔다 */
export function expandOneLevel(index, expanded) {
  const { level } = openLevelOf(index, expanded);
  return new Set([...(expanded instanceof Set ? expanded : []), ...idsToLevel(index, level + 1)]);
}

/**
 * [−] 한 단 접기 — 손으로 더 펼친 가지가 있으면 먼저 level 단으로 고르게 맞추고, 고르면 한 단 올라간다(1단 아래로는 안 간다).
 * 첫 누름이 고른 모양을 만들어야 다음 누름부터 단이 하나씩 바뀐다.
 */
export function collapseOneLevel(index, expanded) {
  const { level, visible } = openLevelOf(index, expanded);
  return idsToLevel(index, visible > level ? level : Math.max(1, level - 1));
}
